import React from 'react';
import { Provider } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from 'shared/api/query-client';
import { logQueryErrorToConsole } from 'shared/api/query-errors';
import monitorService from './helpers/monitor.service';
import { currentEnvironment } from 'shared/services/Environment';
import { ENVIRONMENT_KEYS } from 'shared/constants';
import { configureApiClient } from 'shared/api/api-client';
import { createReduxAuthStrategy } from 'shared/api/redux-auth-strategy';
import App from './components/app/App';
import 'antd/dist/reset.css';

// Self-hosted fonts via Fontsource (imported here to avoid SCSS loader warnings)
import '@fontsource-variable/raleway';
import '@fontsource-variable/raleway/wght-italic.css';
import '@fontsource/inter/100.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/inter/900.css';

import './styles/main.scss';
import { store } from './store/store';
import { ErrorBoundary } from './components/errors/error-boundary.component';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

configureApiClient(createReduxAuthStrategy({ useBearer: false }));

// Outside development the summary goes to Datadog logs through the monitor,
// never the raw axios error: it carries the bearer header and the response body.
const queryClient = createQueryClient(
  {},
  {
    onError:
      currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT
        ? logQueryErrorToConsole
        : record => monitorService.error('react-query error', record),
  },
);

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);
root.render(
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </QueryClientProvider>
  </Provider>,
);

if (import.meta.hot) {
  import.meta.hot.on('lang-update', ({ key, lang }) => {
    const { langManager } = store.getState();
    store.dispatch({
      type: '[Lang Manager] Update repo',
      payload: {
        repo: {
          ...langManager.repo,
          [key]: { ...langManager.repo[key], lang },
        },
      },
    });
  });
}
